<template>
  <div :class="['episode-container', dynamicWidth ? 'self-sizing' : '']" v-press="navigateToEpisode">
    <div class="image-wrapper video-ratio">
      <TG4Image :image="video.image" aspect="landscape" :alt="title || ''" />
      <TG4PlayIcon class="play-icon" />
      <div
        v-if="progressPercent !== null"
        class="progress-container"
      >
        <div class="progress-bar" :style="{ width: `${progressPercent}%` }"></div>
      </div>
    </div>
    <div  class="title-container" :style="textStyle">
      <h2>{{ title }}</h2>
      <h3 v-if="subtitle">{{ subtitle }}</h3>
    </div>
  </div>
</template>

<script setup lang="ts">

import { computed } from 'vue';
import { VideoResponseWithProgress } from '@/types/cms';
import { getCurrentInstance } from 'vue';
import { RouteName, routeTo } from '@/router/routes';
import { VideoFormatter } from '@/helpers/video-formatter';
import TG4Image from '@/components/TG4Image.vue';
import TG4PlayIcon from '../TG4PlayIcon.vue';

const instance = getCurrentInstance();
const router = instance?.proxy?.$router;

const props = defineProps<{
  video: VideoResponseWithProgress;
  dynamicWidth?: boolean;
  textColor?: string;
}>();


const title = computed(() => props.video.seriesTitle);
const subtitle = computed(() => VideoFormatter.getEpisodeCellSubtitle(props.video));

const progressPercent = computed(() => {
  const raw = props.video.playbackProgress;
  if (raw === null || raw === undefined) return null;

  const percent = raw <= 1 ? raw * 100 : raw;
  //Min of 1 so we show a little nubbin
  return Math.min(100, Math.max(1, percent));
});

const navigateToEpisode = () => {
  const navigation = router?.push(routeTo(RouteName.Episode, { videoId: props.video.vid }));
  //Swallow duplicate navigation errors
  navigation?.catch((err) => {
    if (err?.name === 'NavigationDuplicated') return;
    console.log(err);
  });
};

const textStyle = computed(() => {
  if (props.textColor) {
    return {
      color: props.textColor,
    };
  }
  return {};
});
</script>

<style scoped lang="scss">
.episode-container {
  &:focus {

    outline: 0;

    .image-wrapper {
      border-color: $color-pink;
    }

    .title-container {
      @include text-white;
    }
  }
}

.play-icon {
  position: absolute;
  width: 60px;
  height: 60px;
  left: 0;
  bottom: 0;
}

.image-wrapper {
  position: relative;
  margin-top: 5px;
  border: $focus-border-width solid transparent;
  box-sizing: border-box;
}

.progress-container {
  position: absolute;
  left: 60px;
  right: 0;
  bottom: 0;
  height: $progress-bar-height;
  background: $color-off-white;
}

.progress-bar {
  position: absolute;
  left: 0;
  bottom: 0;
  height: $progress-bar-height;
  max-width: calc(100% - 50px - 60px);
  background: $color-pink;
}

.video-ratio {
  width: $episode-cell-width;
}

// Two classes deep so these beat both the rule above and the small-screen
// overrides at the bottom of the file, whatever the source order.
.episode-container.self-sizing {
  .video-ratio {
    width: 100%;
  }

  .title-container,
  .title-container h2 {
    max-width: 100%;
  }
}

.title-container {
  width: 100%;
  text-align: left;
  max-width: 460px;
  margin-bottom: 10px;
  @include text-white;

  h2 {
    margin: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    @include text-cell-title;
  }

  h3 {
    margin: 0;
    @include text-cell-subtitle;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}
</style>
